import React, { useState, useRef } from 'react';
import Icon from '../AppIcon';
import Button from './Button';
import LoadingSpinner from './LoadingSpinner';
import SuccessNotification from './SuccessNotification';
import OpenAIQuotaStatus from './OpenAIQuotaStatus';
import { extractTextFromPDFFile, parseSongFromPDFFile, detectSongDuplicatesWithAI, parseSongFromTextFile } from '../../services/openaiService';

const SongFileImportModal = ({ 
  isOpen, 
  onClose, 
  onImport, 
  existingSongs = [],
  onDuplicatesFound 
}) => {
  const [file, setFile] = useState(null);
  const [isProcessing, setIsProcessing] = useState(false);
  const [error, setError] = useState('');
  const [processStep, setProcessStep] = useState('');
  const [dragOver, setDragOver] = useState(false);
  const [detectedFileType, setDetectedFileType] = useState(null);
  const [notification, setNotification] = useState(null);
  const fileInputRef = useRef(null);

  const resetState = () => {
    setFile(null);
    setError('');
    setProcessStep('');
    setIsProcessing(false);
    setDragOver(false);
    setDetectedFileType(null);
    setNotification(null);
  };

  const handleClose = () => {
    if (!isProcessing) {
      resetState();
      onClose();
    }
  };

  const showNotification = (message, type = 'success', details = null, duration = 5000) => {
    setNotification({
      message,
      type,
      details,
      duration,
      id: Date.now()
    });
  };

  const handleFileSelect = (selectedFile) => {
    setError('');
    setDetectedFileType(null);
    setNotification(null);
    
    // Auto-detect file type based on extension
    const fileName = selectedFile?.name?.toLowerCase() || '';
    let fileType = null;
    
    if (fileName?.endsWith('.pdf')) {
      fileType = 'pdf';
    } else if (fileName?.endsWith('.txt')) {
      fileType = 'txt';
    } else {
      setError('Please select a PDF or TXT file. Only PDF documents and text files are supported.');
      return;
    }

    // Validate file size (max 10MB)
    const maxSize = 10 * 1024 * 1024; // 10MB
    if (selectedFile?.size > maxSize) {
      setError('File is too large. Please select a file smaller than 10MB.');
      return;
    }

    setFile(selectedFile);
    setDetectedFileType(fileType);
  };

  const handleFileInputChange = (event) => {
    const selectedFile = event?.target?.files?.[0];
    if (selectedFile) {
      handleFileSelect(selectedFile);
    }
  };

  const handleDrop = (event) => {
    event?.preventDefault();
    setDragOver(false);
    
    const droppedFile = event?.dataTransfer?.files?.[0];
    if (droppedFile) {
      handleFileSelect(droppedFile);
    }
  };

  const handleDragOver = (event) => {
    event?.preventDefault();
    setDragOver(true);
  };

  const handleDragLeave = (event) => {
    event?.preventDefault();
    setDragOver(false);
  };

  const handleImport = async () => {
    if (!file) {
      setError('Please select a PDF or TXT file first.');
      return;
    }

    if (!detectedFileType) {
      setError('Unable to detect file type. Please select a valid PDF or TXT file.');
      return;
    }

    setIsProcessing(true);
    setError('');
    setNotification(null);

    try {
      let textContent = '';
      let parsedData = null;

      if (detectedFileType === 'pdf') {
        // Step 1: Extract text from PDF file
        setProcessStep('Extracting text from PDF file...');
        textContent = await extractTextFromPDFFile(file);
        
        if (!textContent || textContent?.length < 10) {
          throw new Error('Unable to extract readable content from the PDF file. Please ensure the file contains text and try again.');
        }

        // Step 2: Parse with OpenAI (with enhanced error handling)
        setProcessStep('Analyzing PDF content with AI...');
        parsedData = await parseSongFromPDFFile(textContent);
      } else if (detectedFileType === 'txt') {
        // Step 1: Read text file content
        setProcessStep('Reading text file content...');
        textContent = await readTextFile(file);
        
        if (!textContent || textContent?.length < 10) {
          throw new Error('Text file appears to be empty or too short. Please ensure the file contains readable content.');
        }

        // Step 2: Parse with OpenAI (with enhanced error handling)
        setProcessStep('Analyzing text content with AI...');
        parsedData = await parseSongFromTextFile(textContent);
      }

      // Check if there's an error context (quota exceeded, etc.) and handle gracefully
      if (parsedData?.errorContext) {
        // Show informative status but continue processing
        const isQuotaIssue = parsedData?.errorContext?.isQuotaIssue;
        const statusMessage = isQuotaIssue 
          ? 'AI quota reached - using smart fallback parsing...' :'AI unavailable - using enhanced text extraction...';
        
        setProcessStep(statusMessage);
        console.warn('AI parsing encountered issue:', parsedData?.errorContext);
        
        // Give user a moment to see the status message
        await new Promise(resolve => setTimeout(resolve, 2000));
        
        // Update step to show we're continuing setProcessStep('Processing with enhanced text analysis...');
        await new Promise(resolve => setTimeout(resolve, 1000));
      }

      // Enhanced title validation - accept more flexible titles
      if (!parsedData?.title || 
          parsedData?.title?.trim() === '' ||
          parsedData?.title?.trim() === 'Untitled Song' ||
          parsedData?.title?.trim() === 'Empty File' ||
          parsedData?.title?.trim() === 'Parse Error') {
        
        // If basic parsing also failed to find a title, create a meaningful one
        const timestamp = new Date()?.toLocaleDateString();
        const defaultTitle = `Imported Song - ${timestamp}`;
        
        parsedData.title = defaultTitle;
        parsedData.overview = {
          ...parsedData?.overview,
          songTitle: defaultTitle,
          context: `Imported from ${file?.name} on ${timestamp}`,
          purpose: 'Manual review recommended - title could not be automatically detected'
        };
      }

      // Step 3: Check for duplicates
      setProcessStep('Checking for existing songs...');
      const duplicateCheck = await detectSongDuplicatesWithAI(parsedData, existingSongs);

      if (duplicateCheck?.hasDuplicates) {
        onDuplicatesFound?.(duplicateCheck);
        handleClose();
        return;
      }

      // Step 4: Import the song
      setProcessStep('Importing song data...');
      
      // Add import metadata
      const finalData = {
        ...parsedData,
        importMetadata: {
          fileName: file?.name,
          fileSize: file?.size,
          fileType: detectedFileType,
          importDate: new Date()?.toISOString(),
          parsingMethod: parsedData?.parsedWithAI ? 'AI' : 'Fallback',
          requiresReview: parsedData?.fallbackParsed || parsedData?.errorContext,
          fallbackReason: parsedData?.fallbackReason || null,
          quotaIssue: parsedData?.errorContext?.isQuotaIssue || false
        }
      };
      
      await onImport(finalData);
      
      // Show appropriate success notification
      if (parsedData?.errorContext?.isQuotaIssue) {
        showNotification(
          'File imported successfully!',
          'warning',
          'AI parsing quota reached. File was imported using enhanced text parsing. You may want to review and enhance the imported data.',
          8000
        );
      } else if (parsedData?.fallbackParsed) {
        showNotification(
          'File imported successfully!',
          'info', 
          'AI parsing was unavailable. File was imported using basic text extraction. Please review the imported data.',
          7000
        );
      } else {
        showNotification(
          'File imported successfully!',
          'success',
          `${detectedFileType?.toUpperCase()} file processed with AI analysis and imported to your song database.`,
          5000
        );
      }
      
      // Auto-close after showing notification
      setTimeout(() => {
        handleClose();
      }, 1500);

    } catch (err) {
      console.error(`Error importing ${detectedFileType?.toUpperCase()} file:`, err);
      
      // Enhanced error messages for users
      let userErrorMessage = err?.message || `Failed to import ${detectedFileType?.toUpperCase()} file. Please try again.`;
      
      // Only show error if it's NOT an AI parsing issue (those should be handled gracefully)
      if (!err?.message?.includes('quota') && 
          !err?.message?.includes('rate limit') && 
          !err?.message?.includes('OpenAI') &&
          !err?.message?.includes('Rate limit exceeded')) {
        setError(userErrorMessage);
      } else {
        // Show a more user-friendly message for rate limiting
        if (err?.message?.includes('Rate limit exceeded')) {
          const waitTimeMatch = err?.message?.match(/(\d+)\s+seconds/);
          const waitTime = waitTimeMatch ? waitTimeMatch?.[1] : '30';
          setError(`Please wait ${waitTime} seconds before trying again. This helps manage AI service usage efficiently.`);
        } else {
          setError(`There was an issue with AI parsing, but the file should have been imported using basic parsing. Please check if the song was added to your database.`);
        }
      }
    } finally {
      setIsProcessing(false);
      setProcessStep('');
    }
  };

  // New function to read text files
  const readTextFile = (file) => {
    return new Promise((resolve, reject) => {
      const reader = new FileReader();
      reader.onload = (event) => {
        try {
          const content = event.target.result;
          if (content && content.trim().length > 0) {
            resolve(content.trim());
          } else {
            reject(new Error('Text file is empty or contains no readable content.'));
          }
        } catch (error) {
          reject(new Error('Failed to read text file content.'));
        }
      };
      reader.onerror = () => {
        reject(new Error('Failed to read the text file. Please try again.'));
      };
      reader.readAsText(file, 'UTF-8');
    });
  };

  if (!isOpen) return null;

  return (
    <>
      {/* Notification */}
      {notification && (
        <SuccessNotification
          isVisible={!!notification}
          message={notification?.message}
          type={notification?.type}
          details={notification?.details}
          duration={notification?.duration}
          onClose={() => setNotification(null)}
        />
      )}

      <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
        <div className="bg-white rounded-xl shadow-xl w-full max-w-md mx-4">
          {/* Header */}
          <div className="flex items-center justify-between p-6 border-b border-gray-200">
            <div className="flex items-center space-x-3">
              <div className="w-10 h-10 bg-indigo-100 rounded-lg flex items-center justify-center">
                <Icon name="Upload" size={20} className="text-indigo-600" />
              </div>
              <div>
                <h3 className="text-lg font-semibold text-gray-900">
                  Import Song from File
                </h3>
                <div className="flex items-center space-x-2 mt-1">
                  <p className="text-sm text-gray-600">
                    AI-powered parsing of PDF and TXT documents
                  </p>
                  <OpenAIQuotaStatus className="relative" />
                </div>
              </div>
            </div>
            <button
              onClick={handleClose}
              disabled={isProcessing}
              className="p-2 hover:bg-gray-100 rounded-lg transition-colors disabled:opacity-50"
            >
              <Icon name="X" size={20} className="text-gray-500" />
            </button>
          </div>

          {/* Content */}
          <div className="p-6">
            {/* Processing Status */}
            {isProcessing && (
              <div className="mb-6 p-4 bg-blue-50 border border-blue-200 rounded-lg">
                <div className="flex items-center space-x-3">
                  <LoadingSpinner size="sm" />
                  <div>
                    <p className="text-sm font-medium text-blue-900">
                      Processing your {detectedFileType?.toUpperCase()} file...
                    </p>
                    <p className="text-xs text-blue-700 mt-1">
                      {processStep}
                    </p>
                  </div>
                </div>
              </div>
            )}

            {/* Error Display */}
            {error && (
              <div className="mb-6 p-4 bg-red-50 border border-red-200 rounded-lg">
                <div className="flex items-start space-x-3">
                  <Icon name="AlertTriangle" size={20} className="text-red-600 mt-0.5" />
                  <div>
                    <p className="text-sm font-medium text-red-900">Import Error</p>
                    <p className="text-sm text-red-700 mt-1">{error}</p>
                  </div>
                </div>
              </div>
            )}

            {/* File Upload Area */}
            <div
              className={`border-2 border-dashed rounded-lg p-8 text-center transition-colors ${
                dragOver
                  ? 'border-indigo-400 bg-indigo-50' :'border-gray-300 hover:border-indigo-400 hover:bg-gray-50'
              } ${isProcessing ? 'opacity-50 pointer-events-none' : ''}`}
              onDrop={handleDrop}
              onDragOver={handleDragOver}
              onDragLeave={handleDragLeave}
            >
              {file ? (
                <div className="flex items-center justify-center space-x-3">
                  <Icon 
                    name={detectedFileType === 'pdf' ? 'FileText' : 'File'} 
                    size={24} 
                    className={detectedFileType === 'pdf' ? 'text-red-600' : 'text-blue-600'} 
                  />
                  <div className="text-left">
                    <p className="text-sm font-medium text-gray-900">{file?.name}</p>
                    <p className="text-xs text-gray-500">
                      {(file?.size / 1024 / 1024)?.toFixed(2)} MB • {detectedFileType?.toUpperCase()} file
                    </p>
                  </div>
                  <button
                    onClick={() => {
                      setFile(null);
                      setDetectedFileType(null);
                    }}
                    disabled={isProcessing}
                    className="p-1 hover:bg-gray-200 rounded transition-colors"
                  >
                    <Icon name="X" size={16} className="text-gray-400" />
                  </button>
                </div>
              ) : (
                <div>
                  <Icon name="Upload" size={32} className="text-gray-400 mx-auto mb-3" />
                  <p className="text-sm font-medium text-gray-900 mb-1">
                    Drop your PDF or TXT file here or click to browse
                  </p>
                  <p className="text-xs text-gray-500">
                    PDF documents and text files (.pdf, .txt) are supported. Max 10MB.
                  </p>
                  <input
                    ref={fileInputRef}
                    type="file"
                    accept=".pdf,.txt"
                    onChange={handleFileInputChange}
                    className="hidden"
                  />
                  <Button
                    variant="outline"
                    size="sm"
                    onClick={() => fileInputRef?.current?.click()}
                    disabled={isProcessing}
                    className="mt-3"
                  >
                    Choose File
                  </Button>
                </div>
              )}
            </div>

            {/* Enhanced Instructions */}
            <div className="mt-6 p-4 bg-amber-50 border border-amber-200 rounded-lg">
              <div className="flex items-start space-x-3">
                <Icon name="Info" size={20} className="text-amber-600 mt-0.5 flex-shrink-0" />
                <div>
                  <p className="text-sm font-medium text-amber-900">Smart Import Process</p>
                  <ul className="text-xs text-amber-800 mt-2 space-y-1">
                    <li>• <strong>AI Analysis:</strong> Intelligent extraction of song details, lyrics, and analysis</li>
                    <li>• <strong>Fallback Protection:</strong> Basic parsing continues if AI quota is reached</li>
                    <li>• <strong>Format Support:</strong> PDF documents and text files (.pdf, .txt)</li>
                    <li>• <strong>Duplicate Detection:</strong> Automatically checks for existing songs</li>
                    <li>• <strong>Smart Defaults:</strong> Fills in missing information intelligently</li>
                    <li>• <strong>Review Friendly:</strong> Flags content that may need manual review</li>
                  </ul>
                </div>
              </div>
            </div>
          </div>

          {/* Footer */}
          <div className="flex items-center justify-end space-x-3 p-6 border-t border-gray-200 bg-gray-50 rounded-b-xl">
            <Button
              variant="outline"
              onClick={handleClose}
              disabled={isProcessing}
            >
              Cancel
            </Button>
            <Button
              variant="default"
              onClick={handleImport}
              disabled={!file || isProcessing}
              iconName="Upload"
              iconPosition="left"
            >
              {isProcessing ? 'Processing...' : 'Import Song'}
            </Button>
          </div>
        </div>
      </div>
    </>
  );
};

export default SongFileImportModal;